<template>
    <div class="full-height">
      <transfer-chat-modal
        v-if="showTransferModal"
        @close="showTransferModal=false"
        @confirmTransfer="confirmTransfer"
        :chatId="selectedChat.id"
      />
      <accept-transfer-modal
        v-if="showAcceptTransferModal"
        :notification="transferingChat"
        @close="showAcceptTransferModal=false"
        @confirmAcceptTransfer="confirmAcceptTransfer"
        @ignoreAcceptTransfer="ignoreAcceptTransfer"
      />
      <portlet
        :icon="'fal fa-comment'"
        :title="'Chat'"
        :actionsBlock="true"
        class="h-100 custom-portlet"
      >
        <template slot="portletBody">
          <div class="container-fluid h-100">
            <div class="row h-100" v-if="chats.length > 0 || conversation || conversationFailure">
              <div class="col-xl-3 col-md-3 h-100">
                <chat-list
                  @chatSelected="setSelectedChat"
                  :selectedChat="highlightedChat"
                  :chats="chats"
                  class="h-100"
                >
                </chat-list>
              </div>
              <template v-if="conversation || selectedChat">
                <div class="border-col col-xl-7 col-md-7 h-100">
                  <conversation-pane
                    v-if="conversation"
                    class="h-100"
                    :conversation="conversation"
                    :sending="startingConversation"
                    :sendFailure="conversationFailure"
                    @start="startConversation"
                  />
                  <chat-window
                    v-else
                    ref="chatWindow"
                    :key="selectedChat.id"
                    class="h-100"
                    :chat="selectedChat"
                    @transfer="showTransferModal=true"
                    @sent="afterSend"
                    @confirmTransfer="confirmTransfer"
                    @archiveChat="archiveChat"
                  />
                </div>
                <div class="col-xl-2 col-md-2 h-100">
                  <user-details
                    class="h-100"
                    :user="detailsUser"
                  >
                  </user-details>
                </div>
              </template>
              <div v-else-if="conversationFailure" class="border-col col-xl-7 col-md-7 h-100">
                <p class="conversation-failure p-3">{{ conversationFailure }}</p>
              </div>
            </div>
            <div v-else>
                <span>No chats yet</span>
            </div>
          </div>
        </template>
      </portlet>
    </div>
</template>

<script>
  import Vue from 'vue';
  import ChatList from '../../components/chat/ChatList.vue'
  import ChatWindow from '../../components/chat/ChatWindow.vue'
  import UserDetails from '../../components/chat/UserDetails.vue'
  import TransferChatModal from '../../components/chat/modals/TransferChatModal.vue'
  import AcceptTransferModal from '../../components/chat/modals/AcceptTransferModal.vue'
  import ConversationPane from '../../components/chat/ConversationPane.vue'
  import store from '../../store.js'
  import { Can } from '../../../permission/ability.js'
  import { RIGHTS } from '../../views/roles/rights'
  import { CONFLICT, NOT_FOUND, describeApiOutcome } from '../../utils/apiOutcome.js'
  import { SHOWN_IN_CONVERSATION } from '../../utils/chatMessages.js'
  import { CONVERSATION_MODE } from '../../utils/flexworkerConversation.js'
  import { colleagueHolding } from '../../utils/chatClaim.js'

  const CONVERSATION_START_FAILED = 'The message could not be sent. Please try again.';

  const CONVERSATION_LOAD_FAILED = 'This employee\'s conversation could not be opened. Please try again.';

  const CHAT_LOAD_FAILED = 'This chat could not be opened. Please try again.';

  const CHAT_UNAVAILABLE = 'This chat is no longer available.';

  const CHAT_CLOSED = 'This chat has been closed.';

  function isUnreadFor(chatMessage, userId) {
    return Boolean(chatMessage.sender)
      && chatMessage.sender.id !== userId
      && SHOWN_IN_CONVERSATION.includes(chatMessage.type);
  }

  export default {
    name: 'Chat',
    components: {
      ChatList,
      ChatWindow,
      UserDetails,
      TransferChatModal,
      AcceptTransferModal,
      ConversationPane
    },
    data() {
      return {
        selectedChatId: null,
        chats: [],
        userId: null,
        transferingChat: null,
        showStartModal: false,
        showTransferModal: false,
        showAcceptTransferModal:false,
        conversation: null,
        startingConversation: false,
        conversationFailure: '',
        conversationRequest: 0,
        requestedChatId: null
      }
    },
    computed: {
      selectedChat() {
        const chosen = this.chats.find(chat => chat.id === this.selectedChatId);

        if (chosen) {
          return chosen;
        }

        const somethingSpecificWasAskedFor = this.$route.query.candidate || this.$route.query.chat || this.requestedChatId;

        return somethingSpecificWasAskedFor ? null : this.chats.find(chat => !chat.isNotification) || null;
      },
      highlightedChat() {
        const shownChat = this.conversation && this.conversation.chat;

        return shownChat ? this.chats.find(chat => chat.id === shownChat.id && !chat.isNotification) || null : this.selectedChat;
      },
      detailsUser() {
        return this.conversation ? this.conversation.flexworker : this.selectedChat.participants[0].user;
      }
    },
    watch: {
      '$route.query.candidate'(candidateId) {
        this.openConversation(candidateId);
      },
      '$route.query.chat'(chatId) {
        this.openChat(chatId);
      }
    },
    async mounted() {
      try {
        console.log("mounted");
        const echo = await this.$echo();

        if (this._isDestroyed) {
          return;
        }

        const userId = this.$store.getters.getHourAppUser.id;
        this.userId = userId;

        this._chatChannel = echo.private(`ChatMessage.${userId}`);
        console.log(`ChatMessage.${userId}`)
        this._notificationsChannel = echo.private(`notifications.${userId}`);

        this._chatChannel.listen('MessageSent', ({ chatMessage }) => this.receiveMessage(chatMessage));

        this._notificationsChannel.listen('.NotifyUser', ({ notification }) => this.receiveNotification(notification));

        this._notificationsChannel.listen('ChatRellocateRequestEvent', ({ notification }) => {
          const notificationChat = JSON.parse(notification.content);
          this.chats.push({ ...notificationChat, isNotification: true, notificationId: notification.id });
        });

        this._notificationsChannel.listen('ChatRellocateAcceptedEvent', ({ notification }) => {
          this.removeChatFromList(JSON.parse(notification.content));
        });

        this._notificationsChannel.listen('.ChatClaimed', this.receiveClaim);
      } catch (error) {
        console.error('Failed to initialize Echo in Chat.vue:', error);
      }
    },
    beforeDestroy() {
      if (this._chatChannel) {
        this._chatChannel.stopListening('MessageSent');
      }
      if (this._notificationsChannel) {
        this._notificationsChannel.stopListening('.NotifyUser');
        this._notificationsChannel.stopListening('ChatRellocateRequestEvent');
        this._notificationsChannel.stopListening('ChatRellocateAcceptedEvent');
        this._notificationsChannel.stopListening('.ChatClaimed', this.receiveClaim);
      }
    },
    async created() {
      this.rights = RIGHTS;
      await this.fetchData();
      this.isAdmin = this.$store.state.users.me.admin;
      await this.openConversation(this.$route.query.candidate);
      await this.openChat(this.$route.query.chat);
    },
    methods: {
      Can,
      async fetchData() {
          this.$store.commit('setLoading',true);
          await store.dispatch('chats/getChats', { ApiClient: Vue.prototype.$ApiClient, departments: this.$store.getters.getUserData.departments }).then((res) => {
              if (res.status === 200) {
                  const notAnEarlierConversation = chat => chat.is_workfix_line
                    || (Boolean(chat.owner) && chat.owner.profile_type === 'Client');
                  const onePerEmployee = res.data.data.filter(notAnEarlierConversation);

                  this.keepInViewWhileReplacing(onePerEmployee);
              }
          }).catch((err) => {

          });

          this.$store.commit('setLoading',false)
          
          await store.dispatch('notifications/getNotifications', { ApiClient: Vue.prototype.$ApiClient, departments: this.$store.getters.getUserData.departments }).then((res) => {
              if (res.status === 200) {
                const notifications = res.data.data;
                notifications.forEach((notification) => {
                  if(notification.type === "reallocateRequest") {
                    const notificationChat = JSON.parse(notification.content);
                    this.chats.push({...notificationChat, isNotification: true, notificationId: notification.id});
                  }
                });
              }
          }).catch((err) => {

          });
          
      },
      keepInViewWhileReplacing(freshChats) {
        const inView = this.selectedChat;
        const droppedFromTheList = inView && !freshChats.some(chat => chat.id === inView.id);

        if (droppedFromTheList) {
          this.selectedChatId = inView.id;
        }

        this.chats = droppedFromTheList ? [...freshChats, inView] : freshChats;
      },
      receiveMessage(chatMessage) {
        const chat = this.chats.find(candidate => candidate.id === chatMessage.chat_id);

        if (!chat) {
          this.fetchData();
        } else {
          const beingRead = Boolean(this.selectedChat) && this.selectedChat.id === chat.id;
          chat.last_message = chatMessage;

          if (!beingRead && isUnreadFor(chatMessage, this.userId)) {
            chat.unread = true;
          }

          const sentByTheEmployee = Boolean(chatMessage.sender) && Boolean(chat.participants)
            && chatMessage.sender.id === chat.participants[0].user.id;

          const employeeWroteAgain = sentByTheEmployee && chat.is_workfix_line && chat.status === 'resolved'
            && (chatMessage.type === 'text' || chatMessage.type === 'file');

          const employeeWasHelped = chat.is_workfix_line
            && chatMessage.type === 'close-request' && chatMessage.content === 'helped';

          if (employeeWroteAgain && chatMessage.receiver === null) {
            chat.owner = null;
            chat.status = 'waiting';
          } else if (employeeWroteAgain) {
            chat.status = 'open';
          } else if (employeeWasHelped) {
            this.refreshChat(chat.id);
          }
        }

        if (this.$refs.chatWindow) {
          this.$refs.chatWindow.handleMessage(chatMessage);
        }
      },
      afterSend({ chatId, claimed, refused }) {
        if (refused) {
          this.refreshChat(chatId);
        } else if (claimed) {
          this.fetchData();
        }
      },
      async fetchChat(chatId) {
        return describeApiOutcome(await store.dispatch('chats/getChatConversation', {
          ApiClient: Vue.prototype.$ApiClient,
          chatId
        }));
      },
      async refreshChat(chatId) {
        const outcome = await this.fetchChat(chatId);

        if (outcome.ok) {
          this.ensureListed(outcome.data.data.chat);
        }
      },
      receiveClaim({ chat_id: chatId, owner }) {
        const claimed = this.chats.find(chat => chat.id === chatId && !chat.isNotification);

        if (claimed) {
          claimed.owner = owner;
          claimed.status = 'open';
        }

        this.$root.$emit('refresh-notifications');
      },
      setSelectedChat(chat) {
        if(chat.isNotification){
          this.transferingChat = chat;
          this.showAcceptTransferModal = true;
        } else if (colleagueHolding(chat, this.$store.getters.getHourAppUser)) {
          this.openChat(chat.id);
        } else {
          this.supersedePendingConversationRequests();
          this.conversation = null;
          this.requestedChatId = null;
          this.selectedChatId = chat.id;
          chat.unread = false;
          // Mark chat as read in the store
          this.$store.dispatch('chats/markChatAsRead', { chatId: chat.id });
        }
      },
      startOpening(chatId = null) {
        const request = this.supersedePendingConversationRequests();
        this.conversation = null;
        this.conversationFailure = '';
        this.selectedChatId = null;
        this.requestedChatId = chatId;

        return request;
      },
      supersedePendingConversationRequests() {
        this.conversationRequest += 1;

        return this.conversationRequest;
      },
      isSuperseded(conversationRequest) {
        return conversationRequest !== this.conversationRequest;
      },
      async openConversation(candidateId) {
        const request = this.startOpening();

        if (candidateId) {
          const outcome = describeApiOutcome(await store.dispatch('chats/getFlexworkerConversation', {
            ApiClient: Vue.prototype.$ApiClient,
            candidateId
          }));

          if (!this.isSuperseded(request)) {
            this.showConversation(outcome);
          }
        }

        return request;
      },
      async openChat(chatId) {
        if (!chatId) {
          return;
        }

        const request = this.startOpening(Number(chatId));

        const transferRequest = this.chats.find(chat => chat.isNotification && chat.id === Number(chatId));

        if (transferRequest) {
          this.setSelectedChat(transferRequest);
          return;
        }

        const outcome = await this.fetchChat(chatId);

        if (!this.isSuperseded(request)) {
          this.showChat(outcome);
        }
      },
      showChat(outcome) {
        if (!outcome.ok) {
          this.conversationFailure = outcome.status === NOT_FOUND ? CHAT_UNAVAILABLE : CHAT_LOAD_FAILED;
          return;
        }

        if (outcome.data.data.mode === CONVERSATION_MODE.HISTORY) {
          this.conversationFailure = CHAT_CLOSED;
          return;
        }

        this.showConversation(outcome);
      },
      showConversation(outcome) {
        if (!outcome.ok) {
          this.conversationFailure = CONVERSATION_LOAD_FAILED;
          return;
        }

        const conversation = outcome.data.data;

        if (conversation.mode === CONVERSATION_MODE.REPLY) {
          this.setSelectedChat(this.ensureListed(conversation.chat));
          return;
        }

        this.conversation = conversation;
      },
      async startConversation(content) {
        if (!content.trim() || this.startingConversation) {
          return;
        }

        const candidateId = this.$route.query.candidate;
        const request = this.supersedePendingConversationRequests();
        this.startingConversation = true;
        this.conversationFailure = '';

        const outcome = describeApiOutcome(await store.dispatch('chats/startFlexworkerConversation', {
          ApiClient: Vue.prototype.$ApiClient,
          candidateId,
          content
        }), { fallback: CONVERSATION_START_FAILED });

        this.startingConversation = false;

        if (outcome.ok) {
          const startedChat = this.ensureListed(outcome.data.data);

          if (!this.isSuperseded(request)) {
            this.setSelectedChat(startedChat);
          }

          return;
        }

        if (this.isSuperseded(request)) {
          return;
        }

        if (outcome.status === CONFLICT) {
          const reopening = await this.openConversation(candidateId);

          if (this.isSuperseded(reopening)) {
            return;
          }
        }

        this.conversationFailure = outcome.message;
      },
      receiveNotification(notification) {
        if (notification && notification.type === 'chatCreated') {
          const rang = JSON.parse(notification.content);
          const listed = this.chats.find(chat => chat.id === rang.id && !chat.isNotification);

          this.ensureListed(listed ? { ...rang, unread: listed.unread } : rang);
        }
      },
      ensureListed(freshChat) {
        const listedChat = this.chats.find(chat => chat.id === freshChat.id && !chat.isNotification);

        if (listedChat) {
          return Object.assign(listedChat, freshChat);
        }

        this.chats.push(freshChat);

        return freshChat;
      },
      removeChatFromList(chatToRemove) {
        const idx = this.chats.findIndex(chat => chat.id === chatToRemove.id)
        if (idx !== -1) {
          this.chats.splice(idx, 1)
        }
      },
      async confirmTransfer(params) {
        await store.dispatch('chats/transferChat', 
          { 
            ApiClient: Vue.prototype.$ApiClient, 
            departments: this.$store.getters.getUserData.departments,
            chatId: this.selectedChat.id,
            userIds: params.userIds,
            message: params.message
          }
        ).then((res) => {
          if(res.status === 200) {
            const idx = this.chats.findIndex(chat => chat.id === this.selectedChat.id)
            this.chats[idx].status = "reallocating";
          }
        }).catch((err) => {

        });
      },
      async markAsViewed() {
        await store.dispatch('notifications/markAsViewed', 
          { 
            ApiClient: Vue.prototype.$ApiClient, 
            departments: this.$store.getters.getUserData.departments,
            notificationId: this.transferingChat.notificationId,
          }
        ).then((res) => {
          if(res.status === 200) {
            this.removeChatFromList(this.transferingChat);
            this.transferingChat.isNotification = false;
            this.transferingChat.notificationId = null;
            this.transferingChat.status = "open";
            this.chats.push(this.transferingChat);
            this.transferingChat = null;
          }
        }).catch((err) => {

        });
      },
      async archiveChat(chatId) {
        const idx = this.chats.findIndex(chat => chat.id === chatId);
        this.chats[idx].status = "resolved";
        console.log("archiveChat");

      },
      async confirmAcceptTransfer() {
        const acceptedChatId = this.transferingChat.id;

        await store.dispatch('chats/reallocateAccept', 
          { 
            ApiClient: Vue.prototype.$ApiClient, 
            departments: this.$store.getters.getUserData.departments,
            chatId: acceptedChatId,
          }
        );
        await this.markAsViewed();
        await this.refreshChat(acceptedChatId);
        this.selectedChatId = acceptedChatId;
      },
      async ignoreAcceptTransfer() {
        await this.markAsViewed();
      },
      handleChatMessage(msg) {
        // Forward the message to ChatWindow component
        this.$refs.chatWindow && this.$refs.chatWindow.handleMessage(msg);
      }
    }
  }
</script>

<style scoped>
  .full-height {
    height: 100vh;
    box-sizing: border-box;
    padding-bottom: 120px;
  }
  .custom-portlet {
      height: 100%;
      display: flex;
      flex-direction: column;
  }  
  ::v-deep .m-portlet__body {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
  }
  .conversation-failure {
      color: #D32F2F;
      font-size: 1.2rem;
  }
  .border-col {
      border-width: 0 1px 0 1px;
      border-style: solid;
      border-color: #F1F1F1;
  }
</style>
